import { EnrolmentRoute } from "@/constants/routes/enrolment.ts"
import { PublicRoute } from "@/constants/routes/public.ts"
import { removeLeadingSlash } from "@/lib/utils.ts"

import { isAbsoluteUrl } from "./isAbsoluteUrl"

// Public journeys served from the root (outside /myaccount) must not be
// rewritten under the My Account path.
const rootServedPaths: readonly string[] = [
  EnrolmentRoute.enrolment,
  PublicRoute.ourTariffs,
]

const isRootServedPath = (path: string) =>
  rootServedPaths.some((root) => path === root || path.startsWith(`${root}/`))

export const getHelpWebsiteUrl = (): string => {
  const publicWebsiteUrl = import.meta.env.VITE_HELP_WEBSITE_URL
  return `${publicWebsiteUrl}/`
}

export const getPublicWebsiteUrl = (): string => {
  const publicWebsiteUrl = import.meta.env.VITE_PUBLIC_WEBSITE_URL
  return `${publicWebsiteUrl}/`
}

const pathToWebsiteUrl = (url: string) => {
  const reg = /^\/?myaccount\/?(.*)/i
  const matches = reg.exec(url)
  if (matches) {
    return "/myaccount/" + matches[1]
  }
  return getPublicWebsiteUrl() + url
}

export const ensureStartsWithMyAccountPath = (url: string) => {
  // Normalise to a single leading slash so already-prefixed paths are detected
  // whether or not they carry one (e.g. "myaccount/usage" from a redirect param)
  // and the bare home path "/myaccount" (no trailing slash) isn't double-prefixed.
  const withLeadingSlash = `/${removeLeadingSlash(url)}`
  // Compare on the path only — a query/hash must not defeat the prefix check,
  // otherwise "myaccount?redirect=..." is treated as unprefixed and becomes
  // "/myaccount/myaccount?redirect=..." (a non-existent route).
  const pathOnly = withLeadingSlash.split(/[?#]/)[0]

  if (isRootServedPath(pathOnly)) {
    return withLeadingSlash
  }

  if (pathOnly === "/myaccount" || pathOnly.startsWith("/myaccount/")) {
    return withLeadingSlash
  }

  return "/myaccount/" + removeLeadingSlash(url)
}

export const ensureResolvedWebsiteUrl = (url?: string) => {
  if (!url) {
    return getPublicWebsiteUrl()
  }

  if (isAbsoluteUrl(url)) {
    return url
  }

  return pathToWebsiteUrl(url)
}
